import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  of,
  interval,
  takeUntil,
  switchMap,
  tap,
  Subject,
  startWith,
  catchError,
} from 'rxjs';
import API_END_POINTS from 'src/app/core/constants/apiEndPoints';
import { Notification } from 'src/app/shared/types/notifications/Notification';
import { replaceTokens } from '../../utils/utility-functions';
import { NotificationTokens } from '../../types/notifications/NotificationTokens';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private stopPolling$ = new Subject<void>();
  private latestNotifications: Notification[] = [];

  private _hasNewNotifications$ = new BehaviorSubject<boolean>(false);
  hasNewNotifications$ = this._hasNewNotifications$.asObservable();
  private httpClient = inject(HttpClient);
  private masterNotificationList = new BehaviorSubject<Notification[]>([]);

  startPollingNotifications(): Observable<{ data: Notification[] }> {
    return interval(60000).pipe(
      startWith(0),
      takeUntil(this.stopPolling$),
      switchMap(() => this.fetchNotifications()),
      tap((newNotifs) => {
        if (!this._hasNewNotifications$.value) {
          const isNew = this.isNewNotification(newNotifs.data);
          this._hasNewNotifications$.next(isNew);
        }
        this.latestNotifications = newNotifs.data;
      })
    );
  }

  stopPolling(restart = false) {
    this.stopPolling$.next();
    this.stopPolling$.complete();
    if (restart) {
      this.stopPolling$ = new Subject<void>();
    }
  }

  private isNewNotification(newList: Notification[]): boolean {
    return (
      newList.length > this.latestNotifications.length ||
      newList[0]?.createdDate > this.latestNotifications[0]?.createdDate
    );
  }

  fetchNotifications(): Observable<{ data: Notification[] }> {
    return this.httpClient.get<{ data: Notification[] }>(
      API_END_POINTS.NOTIFICATIONS.FETCH
    );
  }

  markAsRead(notificationId: string): Observable<{ data: Notification[] }> {
    return this.httpClient.put<{ data: Notification[] }>(
      API_END_POINTS.NOTIFICATIONS.MARK_READ(notificationId),
      {}
    );
  }

  set hasNewNotifications(val: boolean) {
    this._hasNewNotifications$.next(val);
  }

  fetchMasterNotifications = () => {
    return this.httpClient
      .get<{ data: Notification[] }>(API_END_POINTS.NOTIFICATIONS.FETCH_MASTER)
      .pipe(
        tap({
          next: (res) => {
            this.masterNotificationList.next(res.data);
          },
        })
      );
  };

  public get masterNotifications(): Notification[] {
    return this.masterNotificationList.value;
  }

  getMasterNotificationByType = (type: string) => {
    return this.masterNotifications.find((el) => el.notificationType === type);
  };

  notifyUserOrGroup = (
    data: ({
      notificationTypeId: string;
      recipientIds: string[];
      recipientEmails: string[];
      recipientGroups: string[];
      message: string;
      isActive: boolean;
      url?: string; 
    } | null)[]
  ) => {
    const filtered = data.filter(Boolean);
    if (!filtered?.length) {
      return of(null);
    }
    return this.httpClient
      .post(API_END_POINTS.NOTIFICATIONS.NOTIFY, {
        notifications: filtered,
      })
      .pipe(
        catchError((_err, _caught) => {
          return of(null);
        })
      );
  };

  buildNotificationPayload(
    type: string,
    values: NotificationTokens,
    recipientIds: string[] = [],
    recipientEmails: string[] = [],
    url?: string,
  ) {
    try {
      const template = this.getMasterNotificationByType(type);
      if (!template)
        throw new Error(`Notification template "${type}" not found`);

      const message = replaceTokens(template.value, values as any);
      const rawGroups = template.recipientGroup
        ? replaceTokens(template.recipientGroup, values as any)
        : '';
      const recipientGroups = rawGroups
        ? rawGroups
            .split('|')
            .map((g) => g.trim())
            .filter(Boolean)
        : [];

      return {
        notificationTypeId: template.uId,
        recipientIds,
        recipientEmails,
        recipientGroups,
        message,
        isActive: true,
        url,
      };
    } catch (err) {
      console.error(err);
      return null;
    }
  }
}
